import React from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { PublishedWithChanges as PublishedWithChangesIcon } from '@mui/icons-material';
import { endOfTomorrow } from 'date-fns';

import {
    useChangeable,
    FullForm
} from '../CommonComponents/Form';

import {
    createOrder,
    getOrder,
    updateOrder
} from '../../api/orders';

import toast from '../../utils/toast';
import { useDispatch } from '../../redux/hooks';
import { resetLastAllOpenJobsFetch } from '../../redux/actions/market';
import { cleanEntityMetadata } from '../../utils/metadata';
import OrderFormFields from './OrderFormComponents/OrderFormFields';
import getDrivingDistance from '../../utils/driving-distance';
import { getFakeCoordinate } from '../../utils/geometry';
import { orderStatus } from '../../utils/status';
import { NotFound } from '../CommonComponents';
import Loading from '../CommonComponents/Fetch/Loading';

async function addDistancesAndFakeLocationCoordinates(entity: { [key: string]: any }) {
    let d;
    try {
        d = await getDrivingDistance(entity);
    } catch {
        d = { drivingDistance: null, drivingDuration: null };
    }
    return ({
        ...entity,
        fakePickupLat: getFakeCoordinate(entity.pickupLat),
        fakePickupLng: getFakeCoordinate(entity.pickupLng),
        fakeDeliveryLat: getFakeCoordinate(entity.deliveryLat),
        fakeDeliveryLng: getFakeCoordinate(entity.deliveryLng),
        drivingDistance: d.drivingDistance,
        drivingDuration: d.drivingDuration
    });
}

export default function OrderForm() {
    const { id } = useParams();
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const hasFetchedRef = React.useRef(false);

    const defaultOrder = React.useMemo(() => ({
        firstPickupDate: endOfTomorrow().toISOString().slice(0, 10),
        lastPickupDate: null,
        firstDeliveryDate: endOfTomorrow().toISOString().slice(0, 10),
        lastDeliveryDate: null,
        firstPickupTime: null,
        lastPickupTime: null,
        firstDeliveryTime: null,
        lastDeliveryTime: null,

        pickupLat: null,
        pickupLng: null,
        pickupAttention: '',
        pickupStreet: '',
        pickupZip: '',
        pickupCity: '',
        pickupCountry: '',
        deliveryLat: null,
        deliveryLng: null,
        deliveryAttention: '',
        deliveryStreet: '',
        deliveryZip: '',
        deliveryCity: '',
        deliveryCountry: '',
        fakePickupLat: null,
        fakePickupLng: null,
        fakeDeliveryLat: null,
        fakeDeliveryLng: null,

        grossWeight: null,
        chargeableWeight: null,
        volume: null,
        loadingMeters: null,
        palletPlaces: null,
        pallets: null,
        // pieces: null,

        isPublic: true,
        showTenantName: true,
        groups: [],
        recipients: [],
        listPrice: null,
        expiresAt: null,
        publicDescription: '',
        privateDescription: '',
        vehicleTypes: [] as string[],
        equipment: [] as string[],
        licencesCertificates: [] as string[],
        customLicencesCertificates: [] as string[],
        sustainability: [] as string[]
    }), []);

    const [entity, onChange] = useChangeable(defaultOrder);

    React.useEffect(() => {
        (async () => {
            if (id) {
                hasFetchedRef.current = true;
                const order = await getOrder(id);
                onChange(order);
            }
        })();
    }, [id, onChange]);

    const onSubmit = React.useCallback(async () => {
        try {
            const cleanedEntity = cleanEntityMetadata(entity);
            const order = await addDistancesAndFakeLocationCoordinates(cleanedEntity);
            if (id) {
                await updateOrder(id, order);
                dispatch(resetLastAllOpenJobsFetch()); // Refetch allJobs to get correct group/watchlist stats
                toast('Uppdraget uppdaterat');
                navigate(-1);
            } else {
                const createdOrder = await createOrder({ ...order, publishDirectly: true });
                toast('Uppdraget publicerat');
                navigate(`/orders/${createdOrder.id}`, { replace: true });
            }
        } catch (e: any) {
            toast(e, `Kunde inte ${id ? 'uppdatera' : 'publicera'} uppdrag, försök igen`);
        }
    }, [entity, id, dispatch, navigate]);

    const onSubmitDraft = React.useCallback(async () => {
        try {
            const cleanedEntity = cleanEntityMetadata(entity);
            const order = await addDistancesAndFakeLocationCoordinates(cleanedEntity);
            const createdOrder = await createOrder(order);
            toast('Uppdraget sparat som utkast');
            navigate(`/orders/${createdOrder.id}`, { replace: true });
        } catch (e: any) {
            toast(e, 'Kunde inte skapa uppdrag, försök igen');
        }
    }, [entity, navigate]);

    if (id) {
        if (!hasFetchedRef.current) {
            return <Loading name="uppdraget" />;
        }
        if (entity.isArchived || ![orderStatus.DRAFTED, orderStatus.PUBLISHED].some((s) => s === entity.status)) {
            return <NotFound />;
        }
    }

    return (
        <FullForm
            title={`${entity.id ? 'Redigera' : 'Skapa'} uppdrag`}
            entity={entity}
            onSubmit={onSubmit}
            submitText={entity.id ? 'Spara' : 'Publicera'}
            onAltSubmit={entity.id ? undefined : onSubmitDraft}
            altSubmitText="Spara utkast"
            submitStartIcon={entity.id ? undefined : <PublishedWithChangesIcon />}
            showBottomSave
            showHelpModal
        >
            <OrderFormFields
                entity={entity}
                onChange={onChange}
            />
        </FullForm>
    );
}
